import { useDate } from '@/composables/useDate.ts'
import { useHttpClient } from '@/composables/useHttpClient'
import { useLogError } from '@/composables/useLogError'
import { useAccountStore } from '@/stores/useAccountStore'
import { useCubeStore } from '@/stores/useCubeStore'
import { useFormsStore } from '@/stores/useFormsStore'
import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'

export interface CubeAddress {
    address: string;
    zip: string;
    city: string;
    country: string;
}

export interface BillingAddress {
    address: string;
    zip: string;
    city: string;
    country: string;
}

export interface PlanSpecs {
    FFF: string;
}

export interface Plan {
    articleId: number;
    name: string;
    directCancellation: string | null;
    directOrder: string | null;
    cancellationNotice: string | null;
    cancellationDate: string;
    commitmentPeriod: string | null;
    standardPrice: number | null;
    vat: number;
    vatPrice: number;
    articleType: string | null;
    numEnergyLines: number;
    nextVatPrice: number | null;
    specs: PlanSpecs;
    maxNumBookings: number;
    iconUrl: string;
    nextPrice?: number
    nextPriceValidFrom?: number
}

export interface Plans {
    con: Plan;
    gen: Plan;
}

export interface CustomerData {
    loaded: boolean;
    error: boolean;
    meterPointId: string;
    contractId: string;
    fullName: string;
    nickName: string;
    phone: string;
    email: string;
    cubeAddress: CubeAddress;
    billingAddress: BillingAddress;
    plans: Plans;
}

export const useSettingsStore = defineStore('settings', () => {
  const { logError } = useLogError()
  const { moment } = useDate()
  const { reemApi, proxyApi } = useHttpClient()

  // --- State ---

  const contractData = reactive({
    loaded: false,
    error: false,
    meterPointId: '' as string,
    contractId: '' as string,
    fullName: '' as string,
    nickName: '' as string,
    phone: '' as string,
    email: '' as string,
    cubeAddress: { address: '' as string, zip: '' as string, city: '' as string, country: '' as string },
    billingAddress: { address: '' as string, zip: '' as string, city: '' as string, country: '' as string },
    plans: {} as Plans,
  })

  const defaultSellingPrice = ref<number | false | undefined>(undefined)
  const nextDefaultSellingPriceInfo = reactive<{ price: number | false; date: Date | false }>({
    price: false,
    date: false,
  })
  const defaultSellingPricesLoaded = ref(false)

  const consumerPrices = ref<Record<string, any>>({})
  const consumerPricesLoaded = ref(false)

  // --- Computed ---

  const minDefaultSellingPrice = computed(() =>
    parseFloat(useAccountStore().tariffSettings?.minPriceNet ?? 0)
  )

  const maxDefaultSellingPrice = computed(() =>
    parseFloat(useAccountStore().tariffSettings?.maxPriceNet ?? 0.1)
  )

  const sellingPriceStepSize = computed(() =>
    parseFloat(useAccountStore().tariffSettings?.stepSize ?? 0.001)
  )

  // Convenience shape used by Consumer.vue
  const defaultSellingPrices = computed(() => ({
    currentPrice: defaultSellingPrice.value,
    nextPrice: nextDefaultSellingPriceInfo.price,
    dateNextPrice: nextDefaultSellingPriceInfo.date,
  }))

  const nextDefaultSellingPrice = computed(() => nextDefaultSellingPriceInfo.price)
  const nextDefaultSellingPriceDate = computed(() => nextDefaultSellingPriceInfo.date)


  // --- Setters ---

  function setContractData(data: Partial<typeof contractData>) {
    Object.assign(contractData, data)

    if (data.cubeAddress) {
      Object.assign(contractData.cubeAddress, data.cubeAddress)
    }

    if (data.billingAddress) {
      Object.assign(contractData.billingAddress, data.billingAddress)
    }
  }

  function setDefaultSellingPrices(data: { currentPrice: any; nextPrice: any; dateNextPrice: any }) {
    defaultSellingPrice.value = data.currentPrice
    nextDefaultSellingPriceInfo.price = data.nextPrice
    nextDefaultSellingPriceInfo.date = data.dateNextPrice
    defaultSellingPricesLoaded.value = true
  }

  function setConsumerPrices(prices: Record<string, any>) {
    consumerPrices.value = prices
    consumerPricesLoaded.value = true
  }

  function updateConsumerPrice(data: {
    cubeName: string
    currentPrice: any
    currentPriceValidUntil: any
    nextPrice: any
    dateNextPrice: any
  }) {
    let customPrice = consumerPrices.value[data.cubeName];

    if(customPrice == undefined) {
      customPrice = {};
      consumerPrices.value[data.cubeName] = customPrice;
    }

    customPrice.currentPrice = data.currentPrice;
    customPrice.currentPriceValidUntil = data.currentPriceValidUntil;
    customPrice.nextPrice = data.nextPrice;
    customPrice.dateNextPrice = data.dateNextPrice;
  }

  function removeConsumerPrice(cubeName: string) {
    const updated = { ...consumerPrices.value }
    delete updated[cubeName]
    consumerPrices.value = updated
  }

  // --- Actions ---

  async function saveDefaultSellingPrice(newPrice: number) {
    const cube = useCubeStore()
    const formsStore = useFormsStore()
    formsStore.setFormState('defaultSellingPrice', 'submitting')
    try {
      const data = new FormData()
      data.append('preis', String(parseFloat(String(newPrice))))
      data.append('cubevertrag', String(cube.name))
      const result = await reemApi.post(`/artikelbasispreis/${cube.name}`, data)

      formsStore.setFormState('defaultSellingPrice', false)

      if (!result?.data) {
        throw new Error('Update base selling price request failed.')
      }

      const priceData = {
        currentPrice: defaultSellingPrice.value,
        nextPrice: false as number | false,
        dateNextPrice: false as Date | false,
      }

      if (result.data.basispreis_next) {
        priceData.nextPrice = result.data.basispreis_next.preis
        priceData.dateNextPrice = moment(result.data.basispreis_next.gueltig_ab, 'X').toDate()
      } else {
        throw new Error('The api result did not contain new price information.')
      }

      setDefaultSellingPrices(priceData)
    } catch (err) {
      logError('Error updating default selling price: ' + err)
    }
  }

  async function saveSpecialSellingPriceForConsumer(data: { price: number; consumer: string }) {
    const cube = useCubeStore()

    try {
      const formData = new FormData()
      formData.append('preis', String(data.price))
      formData.append('cubeid', data.consumer)
      const result = await reemApi.post(`/artikelsonderpreis/${cube.name}`, formData)

      if (!result?.data) {
        throw new Error('Set consumer price request failed.')
      }

      const currentPrice = result.data.sonderpreis_aktuell
        ? parseFloat(result.data.sonderpreis_aktuell.preis)
        : defaultSellingPrice.value

      const priceUpdate = {
        currentPrice,
        cubeName: result.data.sonderpreis_next.cubeidentifiers[0] as string,
        nextPrice: parseFloat(result.data.sonderpreis_next.preis),
        dateNextPrice: moment(result.data.sonderpreis_next.gueltig_ab, 'X').toDate() as Date,
        currentPriceValidUntil: result.data.sonderpreis_aktuell?.gueltig_bis
          ? (moment(result.data.sonderpreis_aktuell.gueltig_bis, 'X').toDate() as Date)
          : undefined,
      }

      updateConsumerPrice(priceUpdate)

      return result
    } catch (err) {
      logError('Error setting consumer price: ' + err)
    }

    return undefined;
  }

  async function removeSpecialPriceForConsumer(cubeName: string) {
    const cube = useCubeStore()
    try {
      const postData = new FormData()
      postData.append('cubeid', cubeName)
      const result = await reemApi.post(`/artikelsonderpreis/beenden/${cube.name}`, postData)

      if (!result?.data) {
        throw new Error(`Error removing special price for cube: ${cubeName}.`)
      }

      await loadConsumerPrices()
    } catch (err) {
      logError('Removing special price failed: ' + err)
    }
  }

  async function loadContractData() {
    const cube = useCubeStore()
    try {
      const result = await reemApi.get(`/cube/zaehlpunkte/${cube.name}`, {
        params: {
          fields:
            'get_vertrag,get_kunde,get_ansprechpartner,get_lieferadresse,get_rechnungsadressekonto,get_preisartikel,get_preise,get_stdvkpreis,get_adressen',
        },
      })
      if (!result?.data) {
        throw new Error('Contract-data request returned unexpected format.')
      }

      const tarifIds = result.data.list
        .map((c: any) => c.tarifartikelid)
        .filter((i: any) => i !== undefined && i !== null)
      const tarifPromises = tarifIds.map((tid: any) =>
        proxyApi.get(`/article/${tid}`, { params: { cubeName: cube.name } })
      )
      const parsedTariffs = (await Promise.all(tarifPromises)).map((t: any) => t.data)

      const data = result.data.list.find(
        (contract: any) => contract.get_vertrag.cubeidentifier === cube.name
      )
      const contract = data.get_vertrag
      const customer = data.get_vertrag.get_kunde
      const cubeAddress = data.get_lieferadresse
      const billingAddress = data.get_vertrag.get_kunde.get_adressen.find(
        (a: any) => a.bezeichnung === 'Hauptadresse'
      )
      const conPod = result.data.list.find((pod: any) => pod.typ === 3)
      const genPod = result.data.list.find((pod: any) => pod.typ === 4)
      const conPlan = conPod?.tarifartikelid
        ? parsedTariffs.find((t: any) => conPod.tarifartikelid === t.articleId)
        : undefined
      const genPlan = genPod?.tarifartikelid
        ? parsedTariffs.find((t: any) => genPod.tarifartikelid === t.articleId)
        : undefined

      // TODO fix tarif names, this should be done in bpe-proxy
      if (conPlan) {
        conPlan.name = conPlan.name
          ?.replace(/P01_3[0-9]{6}/, '')
          ?.replace('P01_Template', '')
          ?.replaceAll('_', ' ')
          ?.trim();
      }
      if (genPlan) {
        genPlan.name = genPlan.name
          ?.replace(/P01_3[0-9]{6}/, '')
          ?.replace('P01_Template', '')
          ?.replaceAll('_', ' ')
          ?.trim();
      }

      setContractData({
        loaded: true,
        meterPointId: data.zaehlpunktnummer,
        contractId: contract.vertragsnummer,
        fullName: customer.name,
        nickName: contract.nickname,
        phone: customer.telefon,
        email: customer.email,
        cubeAddress: {
          address: cubeAddress?.strasse,
          zip: cubeAddress?.plz,
          city: cubeAddress?.ort,
          country: cubeAddress?.land_bezeichnung,
        },
        billingAddress: {
          address: billingAddress?.strasse,
          zip: billingAddress?.plz,
          city: billingAddress?.ort,
          country: billingAddress?.land_bezeichnung,
        },
        plans: { con: conPlan, gen: genPlan },
      })
    } catch (err) {
      logError('Requesting contract data failed: ' + err)
    }
  }

  async function loadConsumerPrices() {
    const cube = useCubeStore()

    try {
      const result = await reemApi.get(`/artikelsonderpreise/${cube.name}`, {
        params: { fields: 'get_konto,get_artikel,get_vertrag,get_kunde' },
      })

      if (!result?.data) {
        throw new Error('Get consumer prices request failed.')
      }

      const prices = result.data.list.reduce((acc: Record<string, any>, row: any) => {
        for (let i = 0; i < row.cubeidentifiers.length; i++) {
          const cubeName = row.cubeidentifiers[i]
          if (!acc[cubeName]) { acc[cubeName] = {} }

          const price = parseFloat(row.preis)

          if (row.zeittyp === 'aktuell') {
            acc[cubeName].currentPrice = price
            acc[cubeName].currentPriceValidUntil = row.gueltig_bis
              ? moment(row.gueltig_bis, 'X').toDate()
              : undefined
          } else if (row.zeittyp === 'next') {
            acc[cubeName].nextPrice = price
            acc[cubeName].dateNextPrice = moment(row.gueltig_ab, 'X').toDate()
            acc[cubeName].currentPriceValidUntil = row.gueltig_bis
              ? moment(row.gueltig_bis, 'X').toDate()
              : undefined
          }
        }

        return acc
      }, {})

      setConsumerPrices(prices)
    } catch (err) {
      logError('Error getting consumer prices: ' + err)
    }
  }

  async function loadDefaultSellingPrice() {
    const cube = useCubeStore()
    try {
      const result = await reemApi.get(`/artikelbasispreis/${cube.name}`)
      if (!result?.data) {
        throw new Error('Default selling price request failed.')
      }
      const data = {
        currentPrice: result.data.basispreis_aktuell
          ? result.data.basispreis_aktuell.preis
          : false as number | false,
        nextPrice: false as number | false,
        dateNextPrice: false as Date | false,
      }
      if (result.data.basispreis_next) {
        data.nextPrice = result.data.basispreis_next.preis
        data.dateNextPrice = moment(result.data.basispreis_next.gueltig_ab, 'X').toDate()
      }
      setDefaultSellingPrices(data)
    } catch (err) {
      logError('Requesting default selling price failed: ' + err)
    }
  }

  // Called by useAuthStore.clearAuth() on logout.
  function reset() {
    Object.assign(contractData, {
      loaded: false, error: false,
      meterPointId: '', contractId: '', fullName: '', nickName: '', phone: '', email: '',
      cubeAddress: { address: '', zip: '', city: '', country: '' },
      billingAddress: { address: '', zip: '', city: '', country: '' },
      plans: {},
    })
    defaultSellingPrice.value = undefined
    nextDefaultSellingPriceInfo.price = false
    nextDefaultSellingPriceInfo.date = false
    defaultSellingPricesLoaded.value = false
    consumerPrices.value = {}
    consumerPricesLoaded.value = false
  }

  return {
    // State
    contractData,
    defaultSellingPrice,
    defaultSellingPricesLoaded,
    consumerPrices,
    consumerPricesLoaded,
    // Computed
    minDefaultSellingPrice,
    maxDefaultSellingPrice,
    sellingPriceStepSize,
    defaultSellingPrices,
    nextDefaultSellingPrice,
    nextDefaultSellingPriceDate,
    // Methods
    // Setters
    setContractData,
    updateConsumerPrice,
    removeConsumerPrice,
    // Actions
    loadContractData,
    loadConsumerPrices,
    loadDefaultSellingPrice,
    saveDefaultSellingPrice,
    saveSpecialSellingPriceForConsumer,
    removeSpecialPriceForConsumer,
    reset,
  }
})
